import { useEffect, useState } from 'react';

import { getStoryScoreDetails } from '../repositories/storyRepository';
import type { StoryElement } from '../types';

/**
 * Give the score-breakdown modal a story that carries its `base_score_details`.
 *
 * Reader-list rows arrive without the key at all: the list defers it (see
 * backend/app/services/reader_row_projection.py) because it is a quarter of the
 * payload and feeds only this modal. Admin surfaces that already hold the blob
 * — the stories table hydrates it from the admin detail endpoint — pass it in
 * and never fetch.
 *
 * Absence, not nullness, is the trigger: a story that was scored to nothing
 * carries the key with a null value, and re-fetching that on every open would
 * be a round trip to learn what the row already said.
 */
export function useHydratedScoreDetails(story: StoryElement): StoryElement {
  const needsFetch = !('base_score_details' in story);
  const [fetched, setFetched] = useState<StoryElement['base_score_details']>(null);

  useEffect(() => {
    if (!needsFetch) return;
    let cancelled = false;
    void (async () => {
      try {
        const details = await getStoryScoreDetails(story.id);
        if (!cancelled) setFetched(details);
      } catch {
        // Leave the modal rendering its "no breakdown" state rather than
        // pushing a fetch error into an observability dialog.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [story.id, needsFetch]);

  return needsFetch ? { ...story, base_score_details: fetched } : story;
}
